import { useState, useCallback, useEffect } from 'react';
import type { ListResponse, ListQuery } from '../types/api';

interface UseCrudPageOptions<T> {
  fetchFn: (q: ListQuery) => Promise<ListResponse<T>>;
  pageSize?: number;
  initialSortCol?: string;
  initialSortDir?: 'Ascending' | 'Descending';
}

export function useCrudPage<T>({ fetchFn, pageSize = 25, initialSortCol = 'Id', initialSortDir = 'Ascending' }: UseCrudPageOptions<T>) {
  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortCol, setSortCol] = useState(initialSortCol);
  const [sortDir, setSortDir] = useState<'Ascending' | 'Descending'>(initialSortDir);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFn({ offset, limit: pageSize, sortCol, sortDirection: sortDir, searchText: searchText || undefined });
      setData(result.entities ?? []);
      setTotalCount(result.numberofEntities ?? 0);
    } catch {
      setToast({ message: 'Failed to load data', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fetchFn, offset, pageSize, sortCol, sortDir, searchText]);

  useEffect(() => { load(); }, [load]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    // Auto-dismiss is owned by the Toast component (defaults to 15s, pauses on hover).
    setToast({ message, type });
  };

  const openCreate = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (item: T) => { setEditingItem(item); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingItem(null); };

  return {
    data, totalCount, loading, offset, searchText, sortCol, sortDir,
    toast, modalOpen, editingItem, pageSize,
    setOffset, setSearchText, setSortCol, setSortDir, setToast,
    load, showToast, openCreate, openEdit, closeModal,
  };
}
